import {
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

let lastOpenedDialog = 0
/** The open ModalDialogs and ConfirmationModals, by the order they opened */
const openDialogs = new Set<number>()

/**
 * Sends focus to `returnFocusRef` when an open dialog closes or unmounts, for a
 * dialog whose opener is gone by then. Focus moves without scrolling, and only
 * to an element still in the document.
 *
 * A hand-off keeps focus with the next dialog: when a ModalDialog or
 * ConfirmationModal opens in the same update as this one closes, focus is left
 * for it to take, instead of passing through `returnFocusRef` first. A dialog
 * that stays open underneath, opened before this one, does not count.
 *
 * Returns the `disableRestoreFocus` value for `Modal`: MUI's own restore is off
 * while the ref takes over. Whether the ref takes over is fixed when the dialog
 * mounts, because MUI's TrapFocus restores focus whenever that prop changes
 * while the dialog is open. The ref passed at close time is the one used.
 */
export const useReturnFocus = (
  open: boolean | undefined,
  returnFocusRef: RefObject<HTMLElement> | undefined
) => {
  const [returnsFocusToRef] = useState(returnFocusRef !== undefined)
  const latestReturnFocusRef = useRef(returnFocusRef)
  const openedDialogRef = useRef(0)

  useLayoutEffect(() => {
    latestReturnFocusRef.current = returnFocusRef
  })

  // Registered in the layout phase, so a dialog that opens in the update that
  // closes another is already listed when the closing dialog's passive cleanup
  // below decides where focus goes.
  useLayoutEffect(() => {
    if (!open) return undefined

    lastOpenedDialog += 1
    const dialog = lastOpenedDialog
    openedDialogRef.current = dialog
    openDialogs.add(dialog)

    return () => {
      openDialogs.delete(dialog)
    }
  }, [open])

  useEffect(() => {
    if (!open || !returnsFocusToRef) return undefined

    const dialog = openedDialogRef.current

    return () => {
      const isHandedOff = Array.from(openDialogs).some(
        (otherDialog) => otherDialog > dialog
      )
      const target = latestReturnFocusRef.current?.current

      if (!isHandedOff && target?.isConnected) {
        target.focus({ preventScroll: true })
      }
    }
  }, [open, returnsFocusToRef])

  return returnsFocusToRef
}
